import { useState, useEffect, useRef } from "react";
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from "./Guesstheimage.module.css";

const challenges = [
  { icon: "🔄", problem: "Stale closures in Firebase listeners", solve: "Firebase's onValue callbacks capture old state from the closure. useState inside them gives you values that are rounds behind — a classic React async trap.", code: `// ❌ Problem — stale state captured in closure\nonValue(ref, (snap) => {\n  setPlayers([...players, snap.val()]); // players is stale!\n});\n\n// ✅ Fix — useRef mirrors the latest state\nconst playersRef = useRef(players);\nuseEffect(() => { playersRef.current = players; }, [players]);\nonValue(ref, (snap) => {\n  setPlayers([...playersRef.current, snap.val()]); // always fresh\n});` },
  { icon: "🎨", problem: "Syncing canvas drawing across clients", solve: "Sending raw ImageData blobs over Firebase was too large and laggy. I needed a lightweight stroke-based protocol that any client could replay correctly on join.", code: `// Push tiny stroke events, not pixel data\npush(strokesRef, {\n  x0, y0, x1, y1, color, size, ts: Date.now()\n});\n\n// Every client replays strokes on mount\nonChildAdded(strokesRef, (snap) => {\n  const s = snap.val();\n  drawQuadraticBezier(ctx, s); // smooth replay\n});` },
  { icon: "⏱️", problem: "60s timer drifting between players", solve: "Each client running its own setInterval drifts seconds apart. Moved timer authority entirely to Firebase — all clients derive time from a single startTime node.", code: `// Host writes one authoritative start time\nset(gameRef, { startTime: serverTimestamp(), duration: 60 });\n\n// All clients compute remaining time from it\nonValue(gameRef, (snap) => {\n  const { startTime, duration } = snap.val();\n  const elapsed = (Date.now() - startTime) / 1000;\n  setTimer(Math.max(0, duration - elapsed));\n});` },
  { icon: "👻", problem: "Ghost players after page refresh", solve: "Players who close their tab stayed in the room as zombies. Fixed using Firebase's onDisconnect hook which auto-removes the player node when their connection drops.", code: `// Register self-destruct immediately on connect\nconst playerRef = ref(db, \`rooms/\${code}/players/\${uid}\`);\nonDisconnect(playerRef).remove();\n\n// Restore session from localStorage on refresh\nconst saved = localStorage.getItem('gti_session');\nif (saved) silentlyRejoinRoom(JSON.parse(saved));` },
  { icon: "🏁", problem: "Race condition on correct guess", solve: "Two players submitting the right answer within milliseconds both got +10 points and the round ended twice. Firebase transactions made the state update atomic.", code: `// runTransaction prevents double-solve\nrunTransaction(roundRef, (current) => {\n  if (!current || current.solved) return;\n  return { ...current, solved: true, winner: playerId, solvedAt: Date.now() };\n});` },
  { icon: "📱", problem: "Touch drawing broken on mobile", solve: "Touch events have no offsetX/Y, fire differently than mouse events, and the browser's default scroll handler hijacks the canvas. Needed careful passive event setup.", code: `canvas.addEventListener('touchmove', (e) => {\n  e.preventDefault();\n  const rect = canvas.getBoundingClientRect();\n  const t = e.touches[0];\n  draw(t.clientX - rect.left, t.clientY - rect.top);\n}, { passive: false });` },
];

const stack = [
  { icon: "⚛️", name: "React 19", color: "ss", why: "Hooks-based component model made multiplayer state manageable. useRef was indispensable for keeping Firebase callbacks in sync with current state without stale closures.", tags: ["useState", "useRef", "useCallback", "useEffect"] },
  { icon: "🔥", name: "Firebase RTDB", color: "sa", why: "Realtime Database gave me WebSocket-level sync without running a server. onValue listeners, onDisconnect cleanup, and runTransaction covered every real-time need.", tags: ["onValue", "push", "runTransaction", "onDisconnect"] },
  { icon: "🧭", name: "React Router v7", color: "sv", why: "Clean URL-based navigation between Home → Lobby → Game → GameOver. Room code lives in the URL so sharing a link drops you straight into a game.", tags: ["createBrowserRouter", "useNavigate", "useParams"] },
  { icon: "🎨", name: "CSS Modules", color: "sl", why: "Scoped class names with zero runtime overhead. No style collisions across 7 components and styles live right next to the component they belong to.", tags: [".module.css", "composes", "scoped classes"] },
  { icon: "📦", name: "Lucide React", color: "sr", why: "Tree-shakeable SVG icon set — only the icons I use get bundled. Consistent stroke width across the entire UI without pulling in a full icon font.", tags: ["tree-shakeable", "SVG icons", "accessible"] },
  { icon: "▲", name: "Vercel", color: "se", why: "Zero-config deploy from GitHub. Preview URLs per branch meant I could test room logic with two browser tabs pointed at different builds simultaneously.", tags: ["CI/CD", "Edge Network", "Preview Deploys"] },
];

const journey = [
  { phase: "Phase 01 — Ideation", title: "Why build a multiplayer drawing game?", body: "I wanted a project that forced me into real-time state sync — the part most tutorials skip. A Skribbl.io clone hit every constraint at once: multiple clients modifying shared state, event-driven UI, hard time boundaries, and real users who break things in creative ways." },
  { phase: "Phase 02 — Data Modeling", title: "Designing the Firebase schema before writing any UI", body: "Spent a full day sketching the RTDB node tree on paper: rooms/{code}/players, /game, /strokes, /chat. Getting the schema right first meant listener logic was never refactored mid-build — which would have cascade-broken everything downstream." },
  { phase: "Phase 03 — Canvas Engine", title: "Building the real-time drawing layer", body: "First attempt pushed full ImageData blobs to Firebase. Way too slow — 100KB+ per frame. Switched to stroke events: tiny {x0,y0,x1,y1,color,size} objects pushed per mouse segment. Clients replay with quadratic bezier curves for smoothness even from choppy input." },
  { phase: "Phase 04 — Game Logic & Bugs", title: "Turn rotation, scoring, and every race condition imaginable", body: "This phase surfaced every concurrency bug. Double-scoring on simultaneous correct guesses (fixed with runTransaction). Timer drift between clients (fixed with serverTimestamp). Ghost players on refresh (fixed with onDisconnect). Each bug taught a different Firebase primitive." },
  { phase: "Phase 05 — Polish & Ship", title: "Mobile, hints, reconnection, and real-world testing", body: "Added touch support (passive: false was the fix), the letter-hint reveal system, localStorage-based session recovery, and the synced 5→0 countdown. Deployed to Vercel, sent room codes to friends, watched it break in ways I hadn't imagined, fixed them." },
];

const learnings = [
  { color: "ll", title: "useRef is not just for DOM refs", text: "The most important React pattern I learned: useRef holds mutable values that survive re-renders without triggering them. It's the only way to give Firebase async callbacks access to current state." },
  { color: "ls", title: "Firebase transactions are non-optional", text: "Any shared mutable state that multiple clients touch simultaneously needs runTransaction, not a plain set(). Learned this the hard way when two players simultaneously guessed correctly and both got scored." },
  { color: "la", title: "Canvas is stateless — plan for it", text: "Canvas has no memory of what it drew. Storing every stroke in Firebase and replaying on mount was the architectural insight that unlocked correct rejoining, room recovery, and spectator mode." },
  { color: "lv", title: "Schema design before code, always", text: "Refactoring a Firebase node structure mid-project means every onValue listener breaks. Sketching the full data tree before writing code saved me from a complete rewrite at Phase 3." },
  { color: "lr", title: "Mobile events are not mouse events", text: "Touch events have no offsetX/Y. The browser's default scroll hijacks canvas touchmove. And passive: false is the only way to call preventDefault. None of this is in the React docs." },
  { color: "le", title: "Test with real users, not just yourself", text: "The ghost player bug, the double-scoring bug, and the tab-crash disconnect bug were all invisible to me solo-testing. Real friends in a real room surfaced every edge case within 10 minutes." },
];

export default function CaseStudy() {
  const [mode, setMode] = useState('web');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const revealRefs = useRef([]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const r = (i) => (el) => { revealRefs.current[i] = el; };

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.wrapper}>
      <div className={styles.grain} />

      

      {/* HERO */}
      <div className={styles.heroWrap}>
        <div className={styles.hero}>
          <div>
            <div className={styles.heroEyebrow}>⬡ Project Case Study</div>
            <h1 className={styles.heroTitle}>
              Building a<br/>
              <span className={styles.dim}>real-time</span><br/>
              <span className={styles.hl}>multiplayer</span><br/>
              drawing game.
            </h1>
            <p className={styles.heroDesc}>
              How I built Guess The Image — a fully serverless, real-time
              Skribbl.io clone using React 19 and Firebase RTDB. Every decision,
              every bug, every fix. No custom backend.
            </p>
            <div className={styles.heroLinks}>
              <a href="https://guess-the-image.vercel.app" className={`${styles.btn} ${styles.btnLime}`} target="_blank" rel="noreferrer">▶ Play Live</a>
              {/* <a href="https://github.com/pavithra-chandran/Guess-The-Image" className={`${styles.btn} ${styles.btnGhost}`} target="_blank" rel="noreferrer">⎔ GitHub Source</a> */}
            </div>
            <div className={styles.heroMeta}>
              <div><div className={styles.metaLabel}>Type</div><div className={styles.metaVal}>Solo Project</div></div>
              <div><div className={styles.metaLabel}>Stack</div><div className={styles.metaVal}>React + Firebase</div></div>
              <div><div className={styles.metaLabel}>Backend</div><div className={styles.metaVal}>None (serverless)</div></div>
              <div><div className={styles.metaLabel}>Deploy</div><div className={styles.metaVal}>Vercel</div></div>
            </div>
          </div>

          <div className={styles.heroTerminal}>
            <img src={require('../../../images/drawing-app-1.png')} alt="Guess The Image preview" className={styles.heroImg} />
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className={styles.statsBanner}>
        {[["400+","Words in bank"],["6","Race conditions fixed"],["0","Backend servers"],["7","React components"],["<100ms","Firebase sync"],["42s","Vercel deploy"]].map(([v,l],i)=>(
          <div key={i} className={styles.sblock}>
            <span className={styles.sval}>{v}</span>
            <div className={styles.slbl}>{l}</div>
          </div>
        ))}
      </div>

      {/* TECH STACK */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={`${styles.reveal}`} ref={r(0)}>
            <div className={styles.chip}>Tech Stack</div>
            <h2 className={styles.sh}>What I built it <span className={styles.shA}>with — and why</span></h2>
            <p className={styles.ssub}>Every tool was chosen deliberately. Here's the reasoning behind each pick, not just a list of logos.</p>
          </div>
          <div className={`${styles.stackGrid} ${styles.reveal}`} ref={r(1)}>
            {stack.map((s, i) => (
              <div key={i} className={`${styles.scard} ${styles[s.color]}`}>
                <span className={styles.sicon}>{s.icon}</span>
                <div className={styles.sname}>{s.name}</div>
                <p className={styles.swhy}>{s.why}</p>
                <div className={styles.tags}>{s.tags.map((t,j) => <span key={j} className={styles.tag}>{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILD JOURNEY */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(5)}>
            <div className={styles.chip}>Build Journey</div>
            <h2 className={styles.sh}>From idea to <span className={styles.shA}>shipped</span> — the honest timeline</h2>
            <p className={styles.ssub}>Five phases, five lessons. Including everything that broke along the way.</p>
          </div>
          <div className={styles.timeline}>
            {journey.map((j, i) => (
              <div key={i} className={`${styles.tli} ${styles.reveal}`} ref={r(6 + i)} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className={styles.tliPhase}>{j.phase}</div>
                <div className={styles.tliTitle}>{j.title}</div>
                <p className={styles.tliBody}>{j.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(15)}>
            <div className={styles.chip}>Challenges & Solutions</div>
            <h2 className={styles.sh}>The hard problems. <span className={styles.shA}>How I solved them.</span></h2>
            <p className={styles.ssub}>Real-time multiplayer is full of edge cases that tutorials never mention. Here are the six that cost me the most time.</p>
          </div>
          <div className={styles.chgrid}>
            {challenges.map((c, i) => (
              <div key={i} className={`${styles.chcard} ${styles.reveal}`} ref={r(16 + i)} style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className={styles.chhead}>
                  <span className={styles.chic}>{c.icon}</span>
                  <div>
                    <div className={`${styles.chlbl} ${styles.chlblP}`}>Problem</div>
                    <div className={styles.chproblem}>{c.problem}</div>
                  </div>
                </div>
                <div className={styles.chbody}>
                  <div className={`${styles.chlbl} ${styles.chlblS}`} style={{ marginBottom: "0.5rem" }}>How I fixed it</div>
                  <p className={styles.chsolve}>{c.solve}</p>
                  <pre className={styles.chcode}>{c.code}</pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(25)}>
            <div className={styles.chip}>Architecture</div>
            <h2 className={styles.sh}>How all the pieces <span className={styles.shA}>connect</span></h2>
            <p className={styles.ssub}>Why the system works without any custom server, and the key design decisions behind it.</p>
          </div>
          <div className={`${styles.archLayout} ${styles.reveal}`} ref={r(26)}>
            <div className={styles.archDiagram}>
              <div className={`${styles.anode} ${styles.anL}`}>React Client (Browser)</div>
              <div className={styles.aarrow}>reads / writes in real-time</div>
              <div className={`${styles.anode} ${styles.anS}`}>Firebase Realtime Database</div>
              <div className={styles.aarrow}>onValue syncs to all clients</div>
              <div className={`${styles.anode} ${styles.anV}`}>rooms / {"{code}"} / players</div>
              <div className={`${styles.anode} ${styles.anA}`}>rooms / {"{code}"} / strokes</div>
              <div className={`${styles.anode} ${styles.anR}`}>rooms / {"{code}"} / game</div>
              <div className={styles.archNotes}>
                {"onDisconnect() → auto-remove player on tab close\n"}
                {"runTransaction() → atomic, race-free score update\n"}
                {"onChildAdded() → stroke-by-stroke canvas replay\n"}
                {"serverTimestamp() → single source of truth for timer"}
              </div>
            </div>
            <div>
              {[
                { q: "Why no WebSocket server?", a: "Firebase RTDB IS WebSockets under the hood — managed infra, auto-scaling, and reconnects built in. I get real-time sync without ops overhead.", tag: "serverless" },
                { q: "Why push strokes, not canvas state?", a: "Full ImageData blobs are ~100KB per frame. A stroke event is under 50 bytes. Stroke replay is lossless, bandwidth-efficient, and works on late-joining clients too.", tag: "performance" },
              ].map((d, i) => (
                <div key={i} className={styles.dec}>
                  <div className={styles.decQ}>{d.q}</div>
                  <p className={styles.decA}>{d.a}</p>
                  <span className={styles.decTag}>{d.tag}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.decRow}>
            {[
                { q: "Why localStorage for session recovery?", a: "Firebase doesn't persist room membership. On refresh, I write the player's ID + room code to localStorage so they silently rejoin without disrupting the game.", tag: "resilience" },
                { q: "Why useRef alongside useState?", a: "React state drives rendering. useRef holds a live mirror of that state inside Firebase async callbacks — where useState values would be stale. Both are necessary.", tag: "correctness" },
            ].map((d, i) => (
              <div key={i} className={styles.dec}>
                <div className={styles.decQ}>{d.q}</div>
                <p className={styles.decA}>{d.a}</p>
                <span className={styles.decTag}>{d.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEY LEARNINGS */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(30)}>
            <div className={styles.chip}>Key Learnings</div>
            <h2 className={styles.sh}>What this project <span className={styles.shA}>taught me</span></h2>
            <p className={styles.ssub}>Things I'd tell myself on day one if I could go back.</p>
          </div>
          <div className={styles.lgrid}>
            {learnings.map((l, i) => (
              <div key={i} className={`${styles.lcard} ${styles[l.color]} ${styles.reveal}`} ref={r(31 + i)} style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className={styles.lnum}>// {String(i + 1).padStart(2, "0")}</div>
                <div className={styles.ltitle}>{l.title}</div>
                <p className={styles.ltext}>{l.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW THE GAME WORKS */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(40)}>
            <div className={styles.chip}>The Game</div>
            <h2 className={styles.sh}>How it <span className={styles.shA}>plays</span> — the full loop</h2>
            <p className={styles.ssub}>The user experience in six steps. Everything else on this page is what's underneath.</p>
          </div>
          <div className={`${styles.howFlow} ${styles.reveal}`} ref={r(41)}>
            {[
              { num:"01", icon:"🏠", title:"Host Creates Room", desc:"Picks a name, sets round count, gets a 6-char code." },
              { num:"02", icon:"👥", title:"Players Join", desc:"Enter code + name. No account or signup needed." },
              { num:"03", icon:"✅", title:"Ready Up", desc:"All players ready → synced 5s countdown fires." },
              { num:"04", icon:"✏️", title:"Draw the Word", desc:"60s timer. Every stroke streams live to all players." },
              { num:"05", icon:"💬", title:"Guess + Get Hints", desc:"Wrong guesses reveal correct letter positions." },
              { num:"06", icon:"🏆", title:"Score & Repeat", desc:"+10 guesser / +5 drawer. Next turn auto-rotates." },
            ].map((s, i) => (
              <div key={i} className={styles.hstep}>
                <div className={styles.hnum}>{s.num}</div>
                <span className={styles.hico}>{s.icon}</span>
                <div className={styles.htitle}>{s.title}</div>
                <p className={styles.hdesc}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCREENSHOTS */}
      <section className={styles.section}>
        <div className={styles.inner}>
          <div className={styles.reveal} ref={r(42)}>
            <div className={styles.chip}>Screenshots</div>
            <h2 className={styles.sh}>Game in <span className={styles.shA}>action</span></h2>
            <p className={styles.ssub}>A look at the lobby, drawing canvas, guessing phase, and scoreboard.</p>
          </div>
          <div className={styles.ssGrid}>
            {[
              { img: require('../../../images/drawing-app-2.png'), label: 'Game Lobby — Room Code Entry' },
              { img: require('../../../images/drawing-app-3.png'), label: 'Drawing Canvas — Live Strokes' },
              { img: require('../../../images/drawing-app-4.png'), label: 'Leaderboard — Round Scores' },
              { img: require('../../../images/drawing-app-5.png'), label: 'Hint System — Letter Reveal' },
              { img: require('../../../images/drawing-app-6.png'), label: 'Chat — Guess Submissions' },
              { img: require('../../../images/drawing-app-7.png'), label: 'Game Over — Final Scores' },
            ].map((s, i) => (
              <div key={i} className={styles.ssCard} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className={styles.ssImgWrap}>
                  <img src={s.img} alt={s.label} className={styles.ssImg} />

                </div>
                <div className={styles.ssLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.cta}>
        <div className={styles.ctaGlow} />
        <h2 className={styles.ctaTitle}>Read the <span>source.</span><br/>Play the game.</h2>
        <p className={styles.ctaSub}>Everything is open source. Fork it, learn from it, deploy your own version, break it in new ways.</p>
        <div className={styles.ctaRow}>
          {/* <a href="https://github.com/pavithra-chandran/Guess-The-Image" className={`${styles.btn} ${styles.btnLime}`} target="_blank" rel="noreferrer">⎔ View on GitHub</a> */}
          <a href="https://guess-the-image.vercel.app" className={`${styles.btn} ${styles.btnGhost}`} target="_blank" rel="noreferrer">▶ Play Live Demo</a>
        </div>
      </section>

      
    </div>
    <div style={{ backgroundColor: '#000000' }}><Footer /></div>
    </>
  );
}

